import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { apiCallWithCache, clearCacheEntry } from '../utils/apiUtils';
import './RolesAndPermission.css';
import { PermissionGuard, AnyPermissionGuard } from '../components/PermissionGuard';
import { PERMISSION_CODES } from '../utils/permissionUtils';
import notificationService from '../CommanService/notificationService';

const RolesAndPermission = () => {
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    GetAllRoles();
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const GetAllRoles = async () => {
    if (!isMountedRef.current) return;
    
    console.log('Fetching roles from API...');
    setLoading(true);
    setError('');
    try {
      const response = await apiCallWithCache('GetAllRoles', () => apiService.GetAllRoles(true));
      console.log('Roles API response:', response);
      if (isMountedRef.current) {
        // Handle case where response might be null due to cache
        if (response && response.data) {
          console.log('Setting roles:', response.data);
          setRoles(response.data);
        } else {
          console.log('Roles already cached, skipping...');
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to load roles');
        console.error('GetAllRoles error:', err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Function to reload roles with cache clearing
  const reloadRoles = async () => {
    console.log('Reloading roles...');
    // Clear the cache for GetAllRoles to force a fresh API call
    clearCacheEntry('GetAllRoles');
    await GetAllRoles();
    console.log('Roles reloaded successfully');
  };
  const DeleteRoleByID = async (id) => {
    try {
      await apiService.DeleteRoleByID(id);
      await reloadRoles(); // Refresh roles with cache clearing
    } catch (err) {
      setError('Failed to delete role');
    } 
  };

  const GetAllPermissions = async () => {
    try {
      const response = await apiService.GetAllPermissions();
      console.log("GetAllPermissions", response);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Filter roles by search
  const filteredRoles = roles.filter(role =>
    String(role.id).includes(search) ||
    (role.role_name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Delete role by ID
 

  const cssLoader = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(255,255,255,0.8)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px'
    }}>
      <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
        Loading Roles and Permissions...
      </div>
      <div className="bouncing-loader">
        <div></div><div></div><div></div>
      </div>
      <style>
        {`
        .bouncing-loader {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          height: 60px;
          gap: 10px;
        }
        .bouncing-loader div {
          width: 18px;
          height: 18px;
          background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
          border-radius: 50%;
          animation: bouncing-loader 0.6s infinite alternate;
          box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
        }
        .bouncing-loader div:nth-child(2) {
          animation-delay: 0.2s;
        }
        .bouncing-loader div:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes bouncing-loader {
          to {
            transform: translateY(-30px) scale(1.1);
            box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
          }
        }
        `}
      </style>
    </div>
  );

  const tableCircleLoader = (
    <tr>
      <td colSpan={4} style={{ textAlign: 'center', padding: 32 }}>
        <div className="circle-table-loader">
          <div></div>
        </div>
        <style>
          {`
          .circle-table-loader {
            display: inline-block;
            width: 40px;
            height: 40px;
          }
          .circle-table-loader div {
            box-sizing: border-box;
            display: block;
            width: 100%;
            height: 100%;
            border: 4px solid #ff6600;
            border-radius: 50%;
            border-top: 4px solid transparent;
            animation: circle-spin 0.8s linear infinite;
          }
          @keyframes circle-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          `}
        </style>
      </td>
    </tr>
  );

  return (
    <div className="roles-container">
      {loading && cssLoader}
             <AddRoleModal 
         open={showModal} 
         onClose={() => { setShowModal(false); setEditRole(null); }} 
         editRole={editRole}
         onRoleSaved={async () => { await reloadRoles(); setShowModal(false); setEditRole(null); }}
       />
      <div className="roles-header">
        <input
          type="text"
          className="roles-search"
          placeholder="Search "
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '350px', marginLeft: 0, marginRight: 16 }}
        />
        <PermissionGuard permission={PERMISSION_CODES.ROLE_ADD} fallback={null}>
          <button className="add-role-btn" onClick={() => setShowModal(true)}>+ Add Role</button>
        </PermissionGuard>
      </div>
      <div className="roles-table-wrapper">
        <table className="roles-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Role Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr><td colSpan={4} style={{ color: 'red', textAlign: 'center' }}>{error}</td></tr>
            ) : filteredRoles.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center' }}>No roles found.</td></tr>
            ) : (
              filteredRoles.map(role => (
                <tr key={role.id}>
                  <td>{role.id}</td>
                  <td>{role.role_name}</td>
                  <td>
                    {role.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">In Active</span>
                    )}
                  </td>
                  <td>
                    <PermissionGuard permission={PERMISSION_CODES.ROLE_EDIT} fallback={null}>
                      <button className="action-btn edit" title="Edit" onClick={() => { console.log('Edit role data:', role); setEditRole(role); setShowModal(true); }}><span role="img" aria-label="edit">✏️</span></button>
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSION_CODES.ROLE_DELETE} fallback={null}>
                      <button className="action-btn delete" title="Delete" onClick={() => DeleteRoleByID(role.id)}><span role="img" aria-label="delete">🗑️</span></button>
                    </PermissionGuard>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="roles-footer">
        <div className="rows-per-page">
          Rows per page:
          <select defaultValue={10}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="pagination-info">
          1-{filteredRoles.length} of {filteredRoles.length}
        </div>
        <div className="pagination-controls">
          <button disabled>{'<'}</button>
          <button disabled>{'>'}</button>
        </div>
      </div>
    </div>
  );
};

export default RolesAndPermission;

// Move the modal component to the bottom for better organization
const AddRoleModal = ({ open, onClose, editRole, onRoleSaved }) => {
  const [roleName, setRoleName] = useState('');
  const [availablePermissions, setAvailablePermissions] = useState([]); // will be array of objects
  const [chosenPermissions, setChosenPermissions] = useState([]); // will be array of objects
  const [status, setStatus] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAvailable, setSelectedAvailable] = useState([]); // array of permission ids
  const [selectedChosen, setSelectedChosen] = useState([]); // array of permission ids

  useEffect(() => {
    if (open) {
      setRoleName(editRole ? editRole.role_name : '');
      setStatus(editRole ? editRole.is_active : true);
      setSearch('');
      setSelectedAvailable([]);
      setSelectedChosen([]);
      apiService.GetAllPermissions().then(res => {
        if (res && Array.isArray(res.data)) {
          if (editRole && Array.isArray(editRole.permissionIDs)) {
            // Map permissionIDs to permission objects
            const chosen = res.data.filter(p => editRole.permissionIDs.includes(p.id));
            const available = res.data.filter(p => !editRole.permissionIDs.includes(p.id));
            setChosenPermissions(chosen);
            setAvailablePermissions(available);
          } else if (editRole && Array.isArray(editRole.permissions) && editRole.permissions[0]?.id) {
            // Fallback: if permissions is array of objects
            const chosen = res.data.filter(p => editRole.permissions.some(ep => ep.id === p.id));
            const available = res.data.filter(p => !editRole.permissions.some(ep => ep.id === p.id));
            setChosenPermissions(chosen);
            setAvailablePermissions(available);
          } else {
            setAvailablePermissions(res.data);
            setChosenPermissions([]);
          }
        }
      });
    }
  }, [open, editRole]);

  const handlePermissionClick = (perm) => {
    setSelectedAvailable(prev =>
      prev.includes(perm.id) ? prev.filter(p => p !== perm.id) : [...prev, perm.id]
    );
  };

  const handleChosenClick = (perm) => {
    setSelectedChosen(prev =>
      prev.includes(perm.id) ? prev.filter(p => p !== perm.id) : [...prev, perm.id]
    );
  };

  const handleMoveRight = () => {
    const toMove = availablePermissions.filter(p => selectedAvailable.includes(p.id));
    setChosenPermissions(prev => [...prev, ...toMove]);
    setAvailablePermissions(prev => prev.filter(p => !selectedAvailable.includes(p.id)));
    setSelectedAvailable([]);
  };

  const handleMoveLeft = () => {
    // Only move selectedChosen back to available
    const toMove = chosenPermissions.filter(p => selectedChosen.includes(p.id));
    setAvailablePermissions(prev => [...prev, ...toMove]);
    setChosenPermissions(prev => prev.filter(p => !selectedChosen.includes(p.id)));
    setSelectedChosen([]);
  };

  const handleChooseAll = () => {
    setChosenPermissions([...chosenPermissions, ...availablePermissions]);
    setAvailablePermissions([]);
    setSelectedAvailable([]);
  };

  const handleRemoveAll = () => {
    setAvailablePermissions([...availablePermissions, ...chosenPermissions]);
    setChosenPermissions([]);
    setSelectedChosen([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation: must have at least one permission
    if (chosenPermissions.length === 0) {
      notificationService.showWarning('Please select at least one permission.');
      return;
    }
    // Prepare payload as per backend requirement
    const permissionIDs = chosenPermissions.map(p => p.id);
    const payload = {
      id: editRole ? editRole.id : 0,
      role_name: roleName,
      permissionIDs,
      is_active: status,
    };
    try {
      let res;
      if (editRole) {
        console.log('Update payload:', payload); // Print payload when updating
        res = await apiService.UpdateRole(payload);
      } else {
        res = await apiService.AddRole(payload);
      }
             console.log('Role API response:', res);
       notificationService.showSuccess(editRole ? 'Role updated successfully!' : 'Role added successfully!');
       console.log('Role saved successfully, calling onRoleSaved callback...');
       if (onRoleSaved) await onRoleSaved();
    } catch (err) {
      console.error('AddRole API error:', err);
      notificationService.showError('Failed to add role.');
    }
  };

  const filteredAvailable = availablePermissions.filter(p => p.permission_name.toLowerCase().includes(search.toLowerCase()));

  if (!open) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>✖</button>
        <h2 className="modal-title">{editRole ? 'Edit Roles and Permission' : 'Add Role'}</h2>
        <form className="add-role-form" onSubmit={handleSubmit}>
          <label className="modal-label">Role Name<span style={{ color: 'red' }}>*</span></label>
          <input className="modal-input" type="text" placeholder="Role Name" value={roleName} onChange={e => setRoleName(e.target.value)} required />

          <div className="modal-section">
            <label className="modal-label">User Permissions<span style={{ color: 'red' }}>*</span></label>
            <div className="permissions-row">
              <div className="permissions-list">
                <div className="permissions-header">Available User Permissions</div>
                <input className="permissions-search" placeholder="Search here" value={search} onChange={e => setSearch(e.target.value)} />
                <div className="permissions-box" style={{ minHeight: 120, maxHeight: 160, overflowY: 'auto' }}>
                  {filteredAvailable.map((perm) => (
                    <div
                      key={perm.id}
                      className={selectedAvailable.includes(perm.id) ? 'permission-item selected' : 'permission-item'}
                      onClick={() => handlePermissionClick(perm)}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', marginBottom: '2px' }}
                    >
                      {perm.permission_name}
                    </div>
                  ))}
                </div>
                <button type="button" className="choose-all-btn" onClick={handleChooseAll}>Choose All →</button>
              </div>
              <div className="permissions-arrows">
                <button type="button" className="arrow-btn" onClick={handleMoveRight}>→</button>
                <button type="button" className="arrow-btn" onClick={handleMoveLeft} disabled={selectedChosen.length === 0}>←</button>
              </div>
              <div className="permissions-list chosen">
                <div className="permissions-header chosen">Chosen User Permissions</div>
                <div className="permissions-box" style={{ minHeight: 120, maxHeight: 160, overflowY: 'auto' }}>
                  {chosenPermissions.map((perm) => (
                    <div
                      key={perm.id}
                      className={selectedChosen.includes(perm.id) ? 'permission-item selected' : 'permission-item'}
                      onClick={() => handleChosenClick(perm)}
                      style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', marginBottom: '2px' }}
                    >
                      {perm.permission_name}
                    </div>
                  ))}
                </div>
                <button type="button" className="remove-all-btn" onClick={handleRemoveAll}>← Remove All</button>
              </div>
            </div>
          </div>

          <div className="modal-section">
            <label className="modal-label">Status</label>
            <div className="status-toggle-row">
              <span className="status-label">{status ? 'Active' : 'In Active'}</span>
              <label className="switch">
                <input type="checkbox" checked={status} onChange={e => setStatus(e.target.checked)} />
                <span className="slider round"></span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save">{editRole ? 'Update' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}; 